import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ref, set, update, remove, push, onValue, onDisconnect, runTransaction, serverTimestamp,
} from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { CallPeer, getIceServers, getCallMedia, getCameraTrack, getScreenTrack } from '../state/RTCService';
import { LogsService } from '../services/LogsService';
import {
    RING_TIMEOUT_MS, DISCONNECT_GRACE_MS, TERMINAL,
    endNotice, mediaErrorMessage, isStaleRing, qualityFromStats, classifyQuality, intervalLoss, callSummaryText,
} from './callLogic';

/** Answer must lead to a live connection within this long, or the call is reported as failed. */
const CONNECT_TIMEOUT_MS = 30_000;
const QUALITY_EVERY_MS = 3_000;

/** One id per browser tab, so two tabs of the same person cannot both answer. */
const DEVICE_ID = (() => {
    try { return crypto.randomUUID(); } catch { return `d${Date.now()}${Math.random().toString(36).slice(2)}`; }
})();

const stopAll = (stream) => stream?.getTracks?.().forEach(t => { try { t.stop(); } catch { /* ignore */ } });

/**
 * One-to-one internal calls: ringing, answering, connecting, the media controls, and
 * every way a call can end. Signalling lives in calls/{callId}; the ring itself in
 * users/{uid}/incoming_call.
 *
 * Call statuses written to calls/{id}/status:
 *   calling → answered → connected → ended
 *   or: declined | missed | busy | failed | cancelled
 */
export function useCallController({ currentUser, resolveUser, chatIdFor, showToast }) {
    const [call, setCallState] = useState(null);
    const [localPreview, setLocalPreview] = useState(null);     // what I am sending as video
    const [previewIsScreen, setPreviewIsScreen] = useState(false);
    const [remoteStream, setRemoteStream] = useState(null);
    const [remoteTick, setRemoteTick] = useState(0);            // re-render when remote tracks mute/unmute
    const [muted, setMuted] = useState(false);
    const [cameraOn, setCameraOn] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [remoteMedia, setRemoteMedia] = useState({});
    const [quality, setQuality] = useState({ level: 'unknown', rttMs: null, lossPct: null, relayed: false });

    const callRef = useRef(null);
    const peerRef = useRef(null);
    const mediaRef = useRef(null);       // mic (+ camera) stream
    const screenRef = useRef(null);      // screen track while sharing
    const timersRef = useRef({});
    const unsubsRef = useRef([]);
    const finishedRef = useRef(false);
    const serverOffsetRef = useRef(0);
    const statsPrevRef = useRef(null);
    const meRef = useRef(currentUser);
    const toastRef = useRef(showToast);
    meRef.current = currentUser;
    toastRef.current = showToast;

    const setCall = useCallback((next) => {
        const value = typeof next === 'function' ? next(callRef.current) : next;
        callRef.current = value;
        setCallState(value);
    }, []);
    const patchCall = useCallback((p) => setCall(c => (c ? { ...c, ...p } : c)), [setCall]);

    const toast = (msg, tone = 'info') => { if (msg) toastRef.current?.(msg, tone); };
    const clearTimer = (k) => { if (timersRef.current[k]) { clearTimeout(timersRef.current[k]); clearInterval(timersRef.current[k]); delete timersRef.current[k]; } };
    const clearTimers = () => Object.keys(timersRef.current).forEach(clearTimer);

    // Server-corrected clock, for spotting stale rings without trusting this PC's clock.
    useEffect(() => onValue(ref(db, '.info/serverTimeOffset'), s => { serverOffsetRef.current = Number(s.val()) || 0; }), []);

    const publishMedia = useCallback((p) => {
        const c = callRef.current; const me = meRef.current;
        if (!c || !me?.uid) return;
        update(ref(db, `calls/${c.id}/media/${me.uid}`), p).catch(() => {});
    }, []);

    // ---- Ending ------------------------------------------------------------------------

    /**
     * End the call locally, and (unless `write` is false) record the outcome.
     * Safe to call any number of times; only the first does anything.
     */
    const finish = useCallback(async (outcome, { write: writeOpt = true, notify = false } = {}) => {
        let write = writeOpt;
        const c = callRef.current;
        if (!c || finishedRef.current) return;
        finishedRef.current = true;
        const me = meRef.current;
        clearTimers();
        unsubsRef.current.forEach(u => { try { u(); } catch { /* ignore */ } });
        unsubsRef.current = [];

        const durationSec = c.connectedAt ? Math.round((Date.now() - c.connectedAt) / 1000) : 0;
        // Hung up while still asking the browser for the microphone: nothing was rung,
        // so there is no record to close, nothing to log and nothing to post.
        const neverRang = c.status === 'preparing';
        if (neverRang) write = false;
        try {
            onDisconnect(ref(db, `calls/${c.id}/status`)).cancel().catch(() => {});
            if (c.role === 'caller') onDisconnect(ref(db, `users/${c.otherUid}/incoming_call`)).cancel().catch(() => {});
            if (write) {
                await update(ref(db, `calls/${c.id}`), {
                    status: outcome, endedAt: serverTimestamp(), endedBy: me?.uid || null,
                    ...(durationSec ? { durationSec } : {}),
                });
            }
            // Stop the ring wherever it still is.
            if (c.role === 'caller') remove(ref(db, `users/${c.otherUid}/incoming_call`)).catch(() => {});
            else if (me?.uid) remove(ref(db, `users/${me.uid}/incoming_call`)).catch(() => {});
        } catch (e) { console.warn('[call] ending write failed:', e?.message); }

        peerRef.current?.close();
        peerRef.current = null;
        stopAll(mediaRef.current); mediaRef.current = null;
        try { screenRef.current?.stop(); } catch { /* ignore */ }
        screenRef.current = null;

        // History: each side logs its own view of the call. A tab that merely stopped
        // ringing because another of my devices answered logs nothing — that device does.
        if (me?.uid && outcome !== 'answered-elsewhere' && !neverRang) {
            const missedHere = c.role === 'callee' && !c.connectedAt && outcome !== 'declined';
            LogsService.logCall(me.uid, {
                id: c.id,
                type: c.role === 'caller' ? 'outgoing' : missedHere ? 'missed' : 'incoming',
                callType: c.type, outcome, durationSec,
                participants: [c.otherUid],
                timestamp: serverTimestamp(),
            });
        }
        // One line in the conversation, written by the caller only so it appears once.
        if (c.role === 'caller' && c.chatId && me?.uid && !neverRang) {
            push(ref(db, `conversations/${c.chatId}/messages`), {
                senderId: 'system', type: 'system', subtype: 'call_summary',
                text: callSummaryText({ type: c.type, outcome, durationSec }),
                callId: c.id, timestamp: serverTimestamp(),
            }).catch(() => {});
        }
        if (notify) {
            const n = endNotice(outcome, c.otherName);
            if (n) toast(n.text, n.tone);
        }

        setCall(null);
        setLocalPreview(null); setPreviewIsScreen(false);
        setRemoteStream(null); setRemoteMedia({});
        setMuted(false); setCameraOn(false); setSharing(false);
        setQuality({ level: 'unknown', rttMs: null, lossPct: null, relayed: false });
        statsPrevRef.current = null;
    }, [setCall]);

    // ---- Watching the call record ----------------------------------------------------------

    const watchCall = useCallback((callId, otherUid) => {
        unsubsRef.current.push(onValue(ref(db, `calls/${callId}/status`), (s) => {
            const status = s.val();
            const c = callRef.current;
            if (!c || c.id !== callId) return;
            if (TERMINAL.has(status)) {
                // Ended by the other side: record nothing more, just explain.
                finish(status, { write: false, notify: c.role === 'caller' });
                return;
            }
            if (c.role === 'callee' && c.status === 'ringing' && (status === 'answered' || status === 'connected')) {
                // Picked up on another device of mine — stop ringing here, quietly.
                finish('answered-elsewhere', { write: false });
                return;
            }
            if (c.role === 'caller' && status === 'answered' && c.status === 'calling') {
                clearTimer('ring');
                patchCall({ status: 'connecting' });
                timersRef.current.connect = setTimeout(() => {
                    if (callRef.current?.status !== 'connected') finish('failed', { notify: true });
                }, CONNECT_TIMEOUT_MS);
            }
        }));
        unsubsRef.current.push(onValue(ref(db, `calls/${callId}/media/${otherUid}`), (s) => {
            setRemoteMedia(s.val() || {});
        }));
    }, [finish, patchCall]);

    const onConnectionState = useCallback((state) => {
        const c = callRef.current;
        if (!c) return;
        if (state === 'connected') {
            clearTimer('connect'); clearTimer('grace');
            if (!c.connectedAt) {
                patchCall({ status: 'connected', connectedAt: Date.now() });
                set(ref(db, `calls/${c.id}/status`), 'connected').catch(() => {});
            } else {
                patchCall({ status: 'connected' });
            }
        } else if (state === 'disconnected') {
            patchCall({ status: 'reconnecting' });
            clearTimer('grace');
            timersRef.current.grace = setTimeout(() => {
                if (callRef.current?.status === 'reconnecting') finish('failed', { notify: true });
            }, DISCONNECT_GRACE_MS);
        } else if (state === 'failed') {
            finish('failed', { notify: true });
        }
    }, [finish, patchCall]);

    const onRemoteStream = useCallback((s) => {
        setRemoteStream(s);
        setRemoteTick(t => t + 1);
    }, []);

    /** Poll connection quality while connected. */
    useEffect(() => {
        if (call?.status !== 'connected' && call?.status !== 'reconnecting') return undefined;
        const id = setInterval(async () => {
            const stats = await peerRef.current?.stats();
            if (!stats) return;
            const q = qualityFromStats(stats);
            const loss = intervalLoss(statsPrevRef.current, q);
            statsPrevRef.current = q;
            const lossPct = loss ?? null;
            setQuality({ level: classifyQuality({ rttMs: q.rttMs, lossPct }), rttMs: q.rttMs, lossPct, relayed: q.relayed });
        }, QUALITY_EVERY_MS);
        return () => clearInterval(id);
    }, [call?.status]);

    // ---- Starting (caller) ------------------------------------------------------------------

    const startCall = useCallback(async (target, type = 'voice') => {
        const me = meRef.current;
        if (!me?.uid) return null;
        if (callRef.current) { toast('You are already on a call.'); return null; }
        const targets = (Array.isArray(target) ? target : [target]).filter(Boolean);
        if (targets.length !== 1) { toast('Group calls are not available yet — call one person at a time.'); return null; }
        const otherUid = String(targets[0]);
        if (otherUid === me.uid || otherUid.startsWith('-')) {   // '-' = a group/channel id, not a person
            toast('Calls are one-to-one. Pick a person to call.');
            return null;
        }
        const video = type === 'video';
        const other = resolveUser?.(otherUid) || {};
        const callId = push(ref(db, 'calls')).key;
        finishedRef.current = false;
        setCall({
            id: callId, role: 'caller', type: video ? 'video' : 'voice', status: 'preparing',
            otherUid, otherName: other.name || 'Colleague', otherPhoto: other.photo || null,
            chatId: chatIdFor(me.uid, otherUid),
        });

        let media;
        try {
            media = await getCallMedia({ video });
        } catch (e) {
            finishedRef.current = true; setCall(null);
            toast(mediaErrorMessage(e, { video }), 'error');
            return null;
        }
        // Hung up while the browser was asking for permission.
        if (finishedRef.current) { stopAll(media); return null; }
        if (media.cameraFallback) toast('Camera unavailable — calling with voice only.');
        mediaRef.current = media;
        const cam = media.getVideoTracks()[0];
        setCameraOn(!!cam);
        setLocalPreview(cam ? new MediaStream([cam]) : null);

        try {
            const { servers } = await getIceServers();
            if (finishedRef.current) { stopAll(media); return null; }      // cancelled while preparing
            const peer = new CallPeer({ callId, role: 'caller', localStream: media, iceServers: servers, onRemoteStream, onConnectionState });
            peerRef.current = peer;
            patchCall({ status: 'calling' });
            // The record AND the offer exist before anyone is rung, so a fast answer works.
            await peer.call({
                id: callId, callerId: me.uid, callerName: me.name || me.displayName || me.email || 'Colleague',
                callerPhoto: me.photo || null, receiverId: otherUid, type: video ? 'video' : 'voice',
                status: 'calling', timestamp: serverTimestamp(), chatId: callRef.current?.chatId || null,
            });
            // Say up front what I am sending. Without this a voice call never said "camera
            // off", so after a screen share stopped the other side kept the last frame.
            publishMedia({ mic: true, cam: !!cam, screen: false });
            // If this tab dies mid-call, stop the ring and end the call for the other side.
            onDisconnect(ref(db, `users/${otherUid}/incoming_call`)).remove();
            onDisconnect(ref(db, `calls/${callId}/status`)).set('ended');
            await set(ref(db, `users/${otherUid}/incoming_call`), {
                id: callId, callerId: me.uid, callerName: me.name || me.displayName || me.email || 'Colleague',
                callerPhoto: me.photo || null, type: video ? 'video' : 'voice', status: 'calling', timestamp: serverTimestamp(),
            });
            watchCall(callId, otherUid);
            timersRef.current.ring = setTimeout(() => {
                if (callRef.current?.status === 'calling') finish('missed', { notify: true });
            }, RING_TIMEOUT_MS);
            return callId;
        } catch (e) {
            console.error('[call] start failed', e);
            toast(`Call failed: ${e?.message || 'unknown error'}`, 'error');
            finish('failed', { write: true });
            return null;
        }
    }, [chatIdFor, finish, onConnectionState, onRemoteStream, patchCall, publishMedia, resolveUser, setCall, watchCall]);

    // ---- Ringing (callee) -------------------------------------------------------------------

    useEffect(() => {
        const uid = currentUser?.uid;
        if (!uid) return undefined;
        const incomingRef = ref(db, `users/${uid}/incoming_call`);
        return onValue(incomingRef, (snap) => {
            const d = snap.val();
            if (!d || d.status !== 'calling' || !d.id) return;      // ending is handled by watchCall
            if (isStaleRing(d.timestamp, Date.now() + serverOffsetRef.current)) {
                remove(incomingRef).catch(() => {});
                return;
            }
            const c = callRef.current;
            if (c) {
                if (c.id === d.id) return;
                // Already on a call: tell the caller, stop this ring.
                set(ref(db, `calls/${d.id}/status`), 'busy').catch(() => {});
                remove(incomingRef).catch(() => {});
                return;
            }
            const other = resolveUser?.(d.callerId) || {};
            finishedRef.current = false;
            setCall({
                id: d.id, role: 'callee', type: d.type === 'video' ? 'video' : 'voice', status: 'ringing',
                otherUid: d.callerId, otherName: d.callerName || other.name || 'Colleague',
                otherPhoto: d.callerPhoto || other.photo || null,
                chatId: chatIdFor(uid, d.callerId),
            });
            watchCall(d.id, d.callerId);
        });
    }, [currentUser?.uid, chatIdFor, resolveUser, setCall, watchCall]);

    const answerCall = useCallback(async () => {
        const c = callRef.current; const me = meRef.current;
        if (!c || c.role !== 'callee' || c.status !== 'ringing' || !me?.uid) return;
        patchCall({ status: 'connecting' });
        // Claim the call for THIS tab. A second device that answers gets told so.
        try {
            const tx = await runTransaction(ref(db, `calls/${c.id}/answeredBy`), cur => (cur ? undefined : DEVICE_ID));
            if (!tx.committed || tx.snapshot.val() !== DEVICE_ID) {
                toast('This call was answered on another device.');
                finish('answered-elsewhere', { write: false });
                return;
            }
        } catch (e) {
            toast('The call ended before it could be answered.');
            finish('ended', { write: false });
            return;
        }
        remove(ref(db, `users/${me.uid}/incoming_call`)).catch(() => {});

        let media;
        try {
            media = await getCallMedia({ video: c.type === 'video' });
        } catch (e) {
            toast(mediaErrorMessage(e, { video: c.type === 'video' }), 'error');
            finish('failed');
            return;
        }
        if (media.cameraFallback) toast('Camera unavailable — joined with voice only.');
        mediaRef.current = media;
        const cam = media.getVideoTracks()[0];
        setCameraOn(!!cam);
        setLocalPreview(cam ? new MediaStream([cam]) : null);

        try {
            const { servers } = await getIceServers();
            const peer = new CallPeer({ callId: c.id, role: 'callee', localStream: media, iceServers: servers, onRemoteStream, onConnectionState });
            peerRef.current = peer;
            await peer.answer();
            publishMedia({ mic: true, cam: !!cam, screen: false });
            onDisconnect(ref(db, `calls/${c.id}/status`)).set('ended');
            await set(ref(db, `calls/${c.id}/status`), 'answered');
            timersRef.current.connect = setTimeout(() => {
                if (callRef.current?.status !== 'connected') finish('failed', { notify: true });
            }, CONNECT_TIMEOUT_MS);
        } catch (e) {
            console.error('[call] answer failed', e);
            toast(e?.message || 'Could not answer the call.', 'error');
            finish('failed');
        }
    }, [finish, onConnectionState, onRemoteStream, patchCall, publishMedia]);

    /** Hang up / decline / cancel — the right outcome depends on where the call is. */
    const endCall = useCallback(() => {
        const c = callRef.current;
        if (!c) return;
        if (c.role === 'callee' && c.status === 'ringing') finish('declined');
        else if (c.role === 'caller' && (c.status === 'calling' || c.status === 'preparing')) finish('cancelled');
        else finish('ended');
    }, [finish]);

    // ---- Media controls ---------------------------------------------------------------------

    const toggleMute = useCallback(() => {
        const mic = mediaRef.current?.getAudioTracks()[0];
        if (!mic) return;
        const nextMuted = mic.enabled;          // currently on → mute
        mic.enabled = !nextMuted;
        setMuted(nextMuted);
        publishMedia({ mic: !nextMuted });
    }, [publishMedia]);

    /** Camera on/off. Works in a voice call too: switching the camera on upgrades it. */
    const toggleVideo = useCallback(async () => {
        const media = mediaRef.current; const peer = peerRef.current;
        if (!media || !peer) return;
        const cam = media.getVideoTracks()[0];
        if (cam) {
            // Off = release the camera (the light goes out), not just send black frames.
            media.removeTrack(cam); cam.stop();
            if (!screenRef.current) await peer.setOutgoingVideo(null).catch(() => {});
            setCameraOn(false);
            if (!screenRef.current) setLocalPreview(null);
            publishMedia({ cam: false });
            return;
        }
        try {
            const track = await getCameraTrack();
            media.addTrack(track);
            if (!screenRef.current) {
                await peer.setOutgoingVideo(track, 'camera');
                setLocalPreview(new MediaStream([track])); setPreviewIsScreen(false);
            }
            setCameraOn(true);
            publishMedia({ cam: true });
        } catch (e) {
            toast(mediaErrorMessage(e, { video: true }), 'error');
        }
    }, [publishMedia]);

    const stopScreenShare = useCallback(async () => {
        const screen = screenRef.current; const peer = peerRef.current;
        if (!screen) return;
        screenRef.current = null;
        try { screen.stop(); } catch { /* ignore */ }
        const cam = mediaRef.current?.getVideoTracks()[0] || null;
        if (peer) await peer.setOutgoingVideo(cam, 'camera').catch(() => {});
        setSharing(false);
        setPreviewIsScreen(false);
        setLocalPreview(cam ? new MediaStream([cam]) : null);
        publishMedia({ screen: false });
    }, [publishMedia]);

    /** Start or stop sharing. Replaces the camera on the same channel — no renegotiation. */
    const shareScreen = useCallback(async () => {
        if (screenRef.current) { await stopScreenShare(); return; }
        const peer = peerRef.current;
        if (!peer || callRef.current?.status !== 'connected') { toast('Screen sharing starts once the call is connected.'); return; }
        let track;
        try {
            track = await getScreenTrack();
        } catch (e) {
            if (e?.name !== 'NotAllowedError') toast(mediaErrorMessage(e, { screen: true }), 'error');   // a cancelled picker is not an error
            return;
        }
        try {
            await peer.setOutgoingVideo(track, 'screen');
        } catch (e) {
            track.stop();
            toast(`Could not share the screen: ${e?.message || 'unknown error'}`, 'error');
            return;
        }
        screenRef.current = track;
        // The browser's own "Stop sharing" button ends the track: restore the camera then.
        track.onended = () => { if (screenRef.current === track) stopScreenShare(); };
        setSharing(true);
        setPreviewIsScreen(true);
        setLocalPreview(new MediaStream([track]));
        publishMedia({ screen: true });
    }, [publishMedia, stopScreenShare]);

    // Leaving the page mid-call: end it properly rather than leaving the other side hanging.
    useEffect(() => {
        const onUnload = () => { if (callRef.current) finish(callRef.current.status === 'calling' ? 'cancelled' : 'ended'); };
        window.addEventListener('pagehide', onUnload);
        return () => window.removeEventListener('pagehide', onUnload);
    }, [finish]);

    // Signing out ends any call.
    useEffect(() => { if (!currentUser?.uid && callRef.current) finish('ended'); }, [currentUser?.uid, finish]);

    // Remote video is worth showing only when frames are actually arriving (an unmuted
    // track) and the other side has not said its camera is off — unless it is sharing.
    // A voice call's video channel is silent, so this stays false until someone shares.
    const remoteVideoTrack = remoteStream?.getVideoTracks?.().find(t => t.readyState === 'live') || null;
    const remoteVideoLive = !!remoteVideoTrack && !remoteVideoTrack.muted
        && (remoteMedia.screen === true || remoteMedia.cam !== false);

    // Memoised: the chat context spreads this to every chat component, and quality
    // updates every few seconds during a call should not re-render all of them.
    return useMemo(() => ({
        call, localPreview, previewIsScreen, remoteStream, remoteTick, remoteVideoLive,
        muted, cameraOn, sharing, remoteMedia, quality,
        startCall, answerCall, endCall, toggleMute, toggleVideo, shareScreen,
    }), [call, localPreview, previewIsScreen, remoteStream, remoteTick, remoteVideoLive,
        muted, cameraOn, sharing, remoteMedia, quality,
        startCall, answerCall, endCall, toggleMute, toggleVideo, shareScreen]);
}
